import { FormEvent, useState, useRef } from 'react';
import SEO from '../components/SEO';
import DatePicker from 'react-datepicker';
import CustomSelect from '../components/CustomSelect';

const PHONE_NUMBER = '6783139600';
const PHONE_DISPLAY = '(678) 313-9600';

const serviceCategories = [
  'Event Makeup',
  'Weddings',
  'One-on-One Lessons',
  'Editorial & Runway',
  'Corporate & Brand',
  'Luxury Masterclasses',
  'Other',
];

export default function Book() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedService, setSelectedService] = useState('');
  const [hasDetailsValue, setHasDetailsValue] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const datePickerRef = useRef<DatePicker>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const name = formData.get('name')?.toString().trim();
    const email = formData.get('email')?.toString().trim();
    const phone = formData.get('phone')?.toString().trim();
    const service = formData.get('service')?.toString().trim();
    const eventDate = formData.get('eventDate')?.toString().trim();
    const location = formData.get('location')?.toString().trim();
    const details = formData.get('details')?.toString().trim();

    if (!name) {
      setStatus('error');
      setErrorMessage('Please let us know who we will be speaking with.');
      return;
    }

    if (!email) {
      setStatus('error');
      setErrorMessage('Please provide your email address.');
      return;
    }

    if (!phone) {
      setStatus('error');
      setErrorMessage('Please provide your phone number.');
      return;
    }

    if (!service) {
      setStatus('error');
      setErrorMessage('Please select the service you are interested in.');
      return;
    }

    if (!eventDate) {
      setStatus('error');
      setErrorMessage('Please select your event date.');
      return;
    }

    if (!location) {
      setStatus('error');
      setErrorMessage('Please provide your event location.');
      return;
    }

    // Submit to API
    try {
      const apiUrl = process.env.REACT_APP_BOOKING_API_URL;

      if (!apiUrl) {
        console.error('API URL not configured');
        setStatus('error');
        setErrorMessage('Booking system is not configured. Please call us directly.');
        return;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          phone,
          service,
          eventDate,
          location,
          details,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit booking request');
      }

      setStatus('success');
      setErrorMessage('');
      form.reset();
      setSelectedDate(null);
      setSelectedService('');
      setHasDetailsValue(false);

    } catch (error) {
      console.error('Booking submission error:', error);
      setStatus('error');
      setErrorMessage('Unable to submit your request. Please call us directly at ' + PHONE_DISPLAY);
    }
  };

  return (
    <>
      <SEO
        title="Book - Request Your Luxury Makeup Session"
        description="Book your on-location makeup session with Let's Face It Studio in Atlanta. Request a callback for weddings, events, editorials, masterclasses, and more."
        keywords="book makeup artist Atlanta, bridal makeup booking, event makeup reservation, luxury makeup concierge"
        canonical="https://letsfaceitcosmetics.com/book"
      />
      <section
        className="fixed top-0 left-0 w-full h-[100dvh] bg-gradient-to-b from-[#f1ede7]
    via-[#ece6de] to-[#e4ded4] text-[#161616] pt-[78px] sm:pt-[90px] md:pt-[132px] pb-[24px] xsm:pb-[18px] sm:pb-[27px] sm2:pb-[42px]
    md:pb-[48px] lg:pb-[55px] lg:mt-2 overflow-y-auto antialiased"
      >
      {/* FIX: Added min-h-full and changed to justify-between on lg screens to spread content vertically */}
      <div className="relative mx-auto flex w-full min-h-full flex-col gap-7 px-4 text-left sm:px-8 
        py-10 xsm:py-10 xsm2:py-13 sm:py-[60px] md:py-10 lg:py-8 xl:py-10 md2:mt-4 md:mt-0
       md:px-12 lg:grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] 
      lg:grid-rows-[auto_1fr] lg:items-start lg:gap-14 lg:px-20 xl:px-24 lg:content-center">
        <header className="order-1 max-w-3xl lg:order-none lg:col-start-1 lg:max-w-2xl lg:py-[3px]">
          <span className="uppercase tracking-[0.35em] text-sm text-[#5b5141] md:text-sm antialiased">
            Booking Concierge
          </span>
          <h1 className="mt-3 lg:mt-5 font-mak text-4xl leading-10 xsm:leading-tight text-[#1b1b1b] md:text-5xl lg:text-[52px] md:leading-[1] antialiased">
            Let&apos;s tailor the perfect beauty experience together.
          </h1>
          <p className="mt-4 lg:mt-12 text-[18px] text-[#3c3c3c] md:text-xl md:-mb-1 lg:mb-0 antialiased">
            We keep every booking personal. Call us directly to secure your date or share a few details below and our
            concierge team will call to curate your session.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="order-2 border border-[#d9cbb8] bg-[#111111]/95 p-6 -mt-1 shadow-[0_30px_80px_-60px_rgba(17,17,17,0.9)] 
          backdrop-blur lg:order-none lg:col-start-2 lg:row-span-2 lg:self-center lg:mt-5 lg:px-7 lg:py-6"
          noValidate
        >
          <fieldset className="grid gap-6 lg:gap-5">
            <legend className="sr-only">Request a call back</legend>

            <div>
              <label htmlFor="name" className="block text-xs uppercase tracking-[0.25em] text-white/60 antialiased">
                Name*
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                className="mt-2 w-full border border-white/20 bg-transparent px-4 py-3 text-base text-white
                 placeholder:text-white/30 focus:border-[#e6d9c6] focus:outline-none focus:ring-0 antialiased"
                placeholder="Who should we address?"
              />
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:gap-5">
              <div>
                <label htmlFor="email" className="block text-xs uppercase tracking-[0.25em] text-white/60 antialiased">
                  Email*
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  className="mt-2 w-full border border-white/20 bg-transparent px-4 py-3 text-base text-white placeholder:text-white/30 focus:border-[#e6d9c6] focus:outline-none focus:ring-0 antialiased"
                  placeholder="For written follow-up"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs uppercase tracking-[0.25em] text-white/60 antialiased">
                  Phone*
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  className="mt-2 w-full border border-white/20 bg-transparent px-4 py-3 text-base text-white
                   placeholder:text-white/30 focus:border-[#e6d9c6] focus:outline-none focus:ring-0 antialiased"
                  placeholder="Where should we call you?"
                />
              </div>
            </div>

            <div>
              <label htmlFor="service" className="block text-xs uppercase tracking-[0.25em] text-white/60 antialiased">
                Desired service*
              </label>
              <div className="mt-2">
                <CustomSelect
                  id="service"
                  name="service"
                  value={selectedService}
                  onChange={setSelectedService}
                  options={serviceCategories}
                  placeholder="Select a service"
                />
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:gap-5">
              <div className="relative">
                <label htmlFor="event-date" className="block text-xs uppercase tracking-[0.25em] text-white/60 antialiased">
                  Event date*
                </label>
                <div className="relative">
                  <DatePicker
                    ref={datePickerRef}
                    id="event-date"
                    name="eventDate"
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    onCalendarOpen={() => setIsCalendarOpen(true)}
                    onCalendarClose={() => setIsCalendarOpen(false)}
                    placeholderText="Select a date"
                    dateFormat="MM/dd/yyyy"
                    minDate={new Date()}
                    className={`mt-2 w-full border border-white/20 px-4 py-3 pr-12 text-base
                    focus:border-[#e6d9c6] focus:outline-none focus:ring-0 antialiased transition-colors ${
                      selectedDate
                        ? 'bg-white text-black'
                        : 'bg-transparent text-white/30 placeholder:text-white/30'
                    }`}
                    wrapperClassName="w-full"
                    popperClassName="date-picker-popper"
                    calendarClassName="luxury-calendar"
                    popperPlacement="bottom-start"
                    popperContainer={({ children }) => (
                      <div className="relative w-full">{children}</div>
                    )}
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isCalendarOpen) {
                        datePickerRef.current?.setOpen?.(false);
                      } else {
                        datePickerRef.current?.setOpen?.(true);
                      }
                    }}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 mt-1 transition-colors pointer-events-auto z-10 ${
                      selectedDate
                        ? 'text-black hover:text-black/70'
                        : 'text-white/60 hover:text-[#e6d9c6]'
                    }`}
                    aria-label={isCalendarOpen ? "Close calendar" : "Open calendar"}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="location" className="block text-xs uppercase tracking-[0.25em] text-white/60 antialiased">
                  Location*
                </label>
                <input
                  id="location"
                  name="location"
                  type="text"
                  autoComplete="address-level2"
                  className="mt-2 w-full border border-white/20 bg-transparent px-4 py-3 text-base text-white placeholder:text-white/30 focus:border-[#e6d9c6] focus:outline-none focus:ring-0 antialiased"
                  placeholder="City, venue, or virtual"
                />
              </div>
            </div>

            <div>
              <label htmlFor="details" className="block text-xs uppercase tracking-[0.25em] text-white/60 antialiased">
                Tell us more
              </label>
              <textarea
                id="details"
                name="details"
                rows={4}
                className={`mt-2 w-full border border-white/20 px-4 py-3 text-base
                focus:border-[#e6d9c6] focus:outline-none focus:ring-0 antialiased transition-colors ${
                  hasDetailsValue
                    ? 'bg-white text-black placeholder:text-black/40'
                    : 'bg-transparent text-white placeholder:text-white/30'
                }`}
                placeholder="Share the vibe, guest count, timing, or any VIP requests."
                onChange={(e) => setHasDetailsValue(e.target.value !== '')}
              />
            </div>
          </fieldset>

          <div className="mt-6 space-y-3 lg:mt-5 lg:space-y-3">
            {status === 'error' && (
              <p className="text-sm text-[#f9d3d3] antialiased" role="alert">
                {errorMessage}
              </p>
            )}
            {status === 'success' && (
              <p className="text-sm text-[#cbbda9] antialiased" role="status">
                Thank you for reaching out. Our concierge will call shortly to confirm the details.
              </p>
            )}

            <button
              type="submit"
              className="w-full border border-[#2a2115] bg-[#cbbda9] px-6 py-3 text-sm uppercase tracking-[0.25em] 
              text-[#1f1b16] transition-all duration-300 hover:bg-[#1f1b16] hover:text-[#cbbda9] hover:border-[#cbbda9] 
              focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-[#cbbda9] antialiased"
            >
              Request a call back
            </button>
          </div>
        </form>

        <aside className="order-3 flex flex-col justify-between space-y-6 border border-[#2a2115] bg-white/80 p-6 
        shadow-[0_30px_80px_-60px_rgba(17,17,17,0.9)] backdrop-blur sm:space-y-8 lg:order-none lg:col-start-1 lg:row-start-2  
        lg:self-center lg:max-w-xl lg:space-y-6 md:-mt-2 lg:mt-0">
          <div className="space-y-4">
            <h2 className="font-mak text-2xl text-[#2a2115] md:text-3xl antialiased">Prefer to speak now?</h2>
            <p className="text-sm text-[#3b3228] md:text-base antialiased">
              Our lead artists handle scheduling personally so every detail is understood from the first hello.
            </p>
            <div className="space-y-2 text-m text-[#5b5141]/90 antialiased">
              <p>- Concierge hours: 9am - 7pm EST</p>
              <p>- Atlanta is home—but for the right event, we'll go anywhere</p>
              <p>- On-call support for active event dates</p>
            </div>
          </div>
          
          <a
            href={`tel:${PHONE_NUMBER}`}
            className="inline-flex items-center justify-center border border-[#2a2115] bg-black
            text-zinc-200 px-6 py-3 text-sm uppercase tracking-[0.25em] transition-all duration-300
            hover:bg-[#dfd2bf] hover:text-[#2a2115] hover:border-[#2a2115] focus-visible:outline focus-visible:outline-offset-2
             focus-visible:outline-[#2a2115] antialiased"
          >
            Call {PHONE_DISPLAY}
          </a>
        </aside>
      </div>
    </section>
    </>
  );
}